import React from 'react'
import { Pill } from '../sso-workspace/pill'
import { isCode, serverMessage } from '../error/server-message'
import '../sso-workspace/sso-workspace.scss'

// The partner API's words and rules on the organization's own pages (Phase
// E; plan rulings E-4, E-5, E-11 and E-18): an API key's system key, a
// member's linked accounts, a group's external id and the memberships the
// partner API manages. The server is the authority: these only shape what
// is offered and say what it answered.

// A system key (E-4): a slug of 1 to 64 lowercase letters, digits and
// dashes, no dash at either end -- core's credentials.ValidSystemKey and
// edxr-cli's CHECKs hold the same rule.
export const SYSTEM_KEY_SHAPE = /^[a-z0-9]([a-z0-9-]{0,62}[a-z0-9])?$/
export const SYSTEM_KEY_RULE = 'A system key is 1 to 64 lowercase letters, digits and dashes, with no dash at either end.'

// What's wrong with a system key typed in (surrounding spaces trimmed), or
// null. Empty is fine unless required.
export const systemKeyProblem = (value, { required = false } = {}) => {
  const s = String(value || '').trim()
  if (!s) return required ? 'Give a system key.' : null
  return SYSTEM_KEY_SHAPE.test(s) ? null : SYSTEM_KEY_RULE
}

// An external id (E-18; a group's follows the same rule): surrounding spaces
// trimmed, then 1 to 255 characters with no control character, otherwise
// kept exactly (case and inner spaces are the partner's).
export const MAX_EXTERNAL_ID_LENGTH = 255
// eslint-disable-next-line no-control-regex
const CONTROL = /[\u0000-\u001f\u007f-\u009f]/

export const externalIDProblem = value => {
  const s = String(value || '').trim()
  if (!s) return 'Give an external id.'
  if ([...s].length > MAX_EXTERNAL_ID_LENGTH) return `An external id can be at most ${MAX_EXTERNAL_ID_LENGTH} characters.`
  if (CONTROL.test(s)) return "An external id can't hold control characters."
  return null
}

// A role made for the partner API (edxr-cli M10's "API partner" preset)
// needs a system key: a key with it and none is refused on every partner
// route (403 partner.system_key_required), and a system key can't be added
// later. Core's role views say so (requires_system_key, from M10's column);
// an older server's carry no flag, and then the preset's name decides.
export const PARTNER_ROLE_NAME = 'API partner'
export const roleNeedsSystemKey = role => {
  if (!role) return false
  if (typeof role.requires_system_key === 'boolean') return role.requires_system_key
  return role.name === PARTNER_ROLE_NAME
}
export const PARTNER_NEEDS_SYSTEM_KEY = 'The partner API needs a system key.'

// Core answers 400 api_key.system_key_required when a key would get such a
// role without a system key: on create, and on an update that moves a key
// onto one. A system key is set only at creation, so the way out is a new
// key. The server's own sentence is for the API's readers; this one is the
// admin's.
export const SYSTEM_KEY_REQUIRED_CODE = 'api_key.system_key_required'
export const SYSTEM_KEY_REQUIRED_MESSAGE = "This role needs a system key. Create a new key with a system key instead; a key's system key can't be added later."

// The line for a failed key create or update: the system-key refusal in the
// admin's words, anything else as serverMessage has it.
export const keyErrorMessage = (error, fallback) =>
  (isCode(error, SYSTEM_KEY_REQUIRED_CODE) ? SYSTEM_KEY_REQUIRED_MESSAGE : serverMessage(error, fallback))

// A linked account's status (E-5): active; suspended (the partner API
// deactivated the member); disputed (held for an admin's review); revoked
// (an admin revoked it: the partner API never makes it again).
const LINK_STATUS = {
  active: { label: 'Active', tone: 'ok' },
  suspended: { label: 'Suspended', tone: 'warn', title: 'The partner API deactivated this member. It can activate them again.' },
  disputed: { label: 'Disputed', tone: 'warn', title: 'Held for review.' },
  revoked: { label: 'Revoked', tone: null, title: 'Revoked by an admin. The partner API never links this external id to this person again.' }
}
export const linkStatusWords = status => LINK_STATUS[status] || { label: String(status || ''), tone: null }

// Where a link came from.
const LINK_SOURCE = { partner_api: 'Partner API', lms_sign_in: 'LMS sign-in', lti_vouched: 'LMS launch', admin: 'An admin' }
export const linkSourceText = source => LINK_SOURCE[source] || String(source || '')

// Who made a link: its API key's name (kept once the key is revoked), "LMS
// connection" for an LMS's, a key since deleted, else nothing.
export const linkCreatedBy = link => {
  if (!link) return ''
  if (link.api_key && link.api_key.name) return link.api_key.name
  if (link.lms_connection) return 'LMS connection'
  if (link.source === 'partner_api') return 'A deleted API key'
  return ''
}

// A group membership the partner API manages (edxr-cli M11
// group_users.managed_by_system_key; content's group-user reads carry it as
// managedBySystemKey): its next sync may remove it. A person's add of the
// same membership takes it over, and then it is the organization's. So does
// clearing or remapping the group's external id (ruling E-23): content
// releases the partner's markers on that group's memberships, so a
// membership read as managed is one in a group still mapped to its system key.
export const MANAGED_BY_PARTNER_TIP = 'The partner API sets this membership; it may remove it at its next sync. Adding it yourself keeps it.'

export const ManagedByPartnerChip = () => <Pill tone="info" title={MANAGED_BY_PARTNER_TIP}>Managed by partner</Pill>

export const isManagedByPartner = membership => !!(membership && membership.managedBySystemKey)

// The row action that adds such a membership as the organization's own.
export const KEEP_MEMBERSHIP_TIP = "Add this membership yourself, so the partner API's sync never removes it."
